import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Divider, Flex, Typography } from 'antd';
import { SbDrawer, SbButton, SbCheckbox, SbSpin, SbTable, SbSelect, SbInput } from 'uiCore';
import useProfileDrawer from './useProfileDrawer';
import {
  EntitlementListWrapperStyled,
  DrawerHeaderRowStyled,
  DescriptionBlockStyled,
  AccessSelectorsWrapperStyled,
  AccessRowStyled,
  DrawerLoadingStyled,
  PROFILE_SELECT_WIDTH,
} from './ProfileManagementV2.styles';
import { capitalizeEachWord, getChainedAccessOptions, filterSelectedByValidOptions } from 'Utils/profileAccess.utils';
import { AuthContext } from 'State/AuthContext';
import Permission from 'Components/Permission';

const { Text } = Typography;

const ProfileDrawerV2 = ({
  open,
  profile,
  featureId,
  featureName,
  profiles,
  onClose,
  onSaveSuccess,
}) => {
  const {
    sortedGroups,
    roleEntitlementData,
    loading,
    saving,
    loadEntitlements,
    toggleGroup,
    isGroupSelected,
    selectAll,
    removeAll,
    isAllSelected,
    isNoneSelected,
    saveEntitlements,
  } = useProfileDrawer();

  const { orgMetaData } = useContext(AuthContext);

  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Access selector state (only used for Jobs Library)
  const [description, setDescription] = useState('');
  const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);

  useEffect(() => {
    if (open && profile) {
      setSelectedRoleId(profile.role_id);
    }
  }, [open, profile]);

  useEffect(() => {
    if (roleEntitlementData) {
      setDescription(roleEntitlementData.description || '');
      setSelectedLegalEntities(roleEntitlementData.legalEntity || []);
      setSelectedBusinessUnits(roleEntitlementData.businessUnit || []);
      setSelectedVerticals(roleEntitlementData.function || []);
      setSelectedTeams(roleEntitlementData.subFunction || []);
    }
  }, [roleEntitlementData]);

  useEffect(() => {
    if (open && selectedRoleId && featureId) {
      loadEntitlements({ roleId: selectedRoleId, featureId });
    }
  }, [open, selectedRoleId, featureId, loadEntitlements]);

  const isDefault = profile?.default_role;
  const isJobsLibraryFeature = (featureName || '').toUpperCase() === 'JOBS LIBRARY';

  const structure = orgMetaData?.organization_structure || [];

  const legalEntityOptions = useMemo(
    () => structure.map((item) => ({ value: item.legal_entity, label: item.legal_entity })),
    [structure],
  );

  const { businessUnitOptions, verticalOptions, teamOptions } = useMemo(
    () =>
      getChainedAccessOptions(structure, {
        legal: selectedLegalEntities,
        business: selectedBusinessUnits,
        vertical: selectedVerticals,
      }),
    [structure, selectedLegalEntities, selectedBusinessUnits, selectedVerticals],
  );

  const handleHierarchyChange = (level, newList) => {
    if (level === 'legal') setSelectedLegalEntities(newList);
    if (level === 'business') setSelectedBusinessUnits(newList);
    if (level === 'vertical') setSelectedVerticals(newList);

    const fresh = getChainedAccessOptions(structure, {
      legal: level === 'legal' ? newList : selectedLegalEntities,
      business: level === 'business' ? newList : selectedBusinessUnits,
      vertical: level === 'vertical' ? newList : selectedVerticals,
    });

    setSelectedBusinessUnits((prev) => filterSelectedByValidOptions(prev, fresh.businessUnitOptions));
    setSelectedVerticals((prev) => filterSelectedByValidOptions(prev, fresh.verticalOptions));
    setSelectedTeams((prev) => filterSelectedByValidOptions(prev, fresh.teamOptions));
  };

  const handleSave = async () => {
    const success = await saveEntitlements({
      roleId: selectedRoleId,
      featureId,
      description: isJobsLibraryFeature ? description : '',
      legalEntity: isJobsLibraryFeature ? selectedLegalEntities : [],
      businessUnit: isJobsLibraryFeature ? selectedBusinessUnits : [],
      func: isJobsLibraryFeature ? selectedVerticals : [],
      subFunction: isJobsLibraryFeature ? selectedTeams : [],
    });
    if (success) {
      onSaveSuccess?.();
      onClose();
    }
  };

  const profileSelectOptions = useMemo(
    () =>
      (profiles || []).map((p) => ({
        value: p.role_id,
        label: p.role_name,
      })),
    [profiles],
  );

  const selectedProfileName = useMemo(() => {
    const found = (profiles || []).find((p) => p.role_id === selectedRoleId);
    return found?.role_name || profile?.role_name || '';
  }, [profiles, selectedRoleId, profile]);

  const entitlementColumns = useMemo(
    () => [
      {
        title: 'Features',
        key: 'feature',
        dataIndex: 'groupName',
        // Cell prefixes a row number; the filter lists the plain group name.
        filterLabel: (groupName) => groupName,
        render: (_, record, index) => `${index + 1}. ${record.groupName}`,
      },
      {
        title: 'Access',
        key: 'access',
        align: 'center',
        width: 100,
        render: (_, record) => (
          <SbCheckbox
            checked={isGroupSelected(record)}
            onChange={() => toggleGroup(record)}
            disabled={isDefault}
          />
        ),
      },
    ],
    [isGroupSelected, toggleGroup, isDefault],
  );

  const footer = (
    <Permission resource="profile" actions={['edit']}>
      <Flex justify="flex-end" gap={8}>
        <SbButton buttonType="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </SbButton>
        {!isDefault && (
          <SbButton buttonType="primary" onClick={handleSave} loading={saving}>
            Save
          </SbButton>
        )}
      </Flex>
    </Permission>
  );

  return (
    <SbDrawer
      open={open}
      onClose={onClose}
      title={`Configure ${capitalizeEachWord(featureName)} Access - ${selectedProfileName || 'New Profile'}`}
      width="50vw"
      footer={footer}
      destroyOnClose
    >
      {loading ? (
        <DrawerLoadingStyled>
          <SbSpin />
        </DrawerLoadingStyled>
      ) : (
        <>
          {/* ── Select all / Remove all + Profile selector ── */}
          <DrawerHeaderRowStyled>
            <Flex gap={24}>
              <SbCheckbox checked={isAllSelected()} onChange={selectAll} disabled={isDefault}>
                Select all
              </SbCheckbox>
              <SbCheckbox checked={isNoneSelected()} onChange={removeAll} disabled={isDefault}>
                Remove all
              </SbCheckbox>
            </Flex>

            {!isDefault && (
              <SbSelect
                value={selectedRoleId}
                onChange={setSelectedRoleId}
                options={profileSelectOptions}
                optionFilterProp="label"
                showSearch
                style={{ width: PROFILE_SELECT_WIDTH }}
                placeholder="Select Profile"
              />
            )}
          </DrawerHeaderRowStyled>

          <Divider />

          {/* ── Profile Description + Access selectors — ONLY for Jobs Library ── */}
          {isJobsLibraryFeature && (
            <>
              <DescriptionBlockStyled>
                <Text strong>Profile Description</Text>
                {!isDefault && !roleEntitlementData?.description ? (
                  <SbInput
                    placeholder="Enter profile description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                ) : (
                  <div className="description-value">{description || '-'}</div>
                )}
              </DescriptionBlockStyled>

              <AccessSelectorsWrapperStyled>
                <AccessRowStyled>
                  <Text strong className="access-label">
                    Access to Legal Entity/s
                    <br />
                    <Text type="secondary" className="access-hint">(select ALL that apply)</Text>
                  </Text>
                  <SbSelect
                    mode="multiple"
                    value={selectedLegalEntities}
                    onChange={(val) => handleHierarchyChange('legal', val)}
                    options={legalEntityOptions}
                    disabled={isDefault}
                    placeholder="Select Legal Entity"
                    maxTagCount="responsive"
                  />
                </AccessRowStyled>

                <AccessRowStyled>
                  <Text strong className="access-label">
                    Access to Business Unit/s
                    <br />
                    <Text type="secondary" className="access-hint">(select ALL that apply)</Text>
                  </Text>
                  <SbSelect
                    mode="multiple"
                    value={selectedBusinessUnits}
                    onChange={(val) => handleHierarchyChange('business', val)}
                    options={businessUnitOptions}
                    disabled={isDefault || selectedLegalEntities.length === 0}
                    placeholder="Select Business Unit"
                    maxTagCount="responsive"
                  />
                </AccessRowStyled>

                <AccessRowStyled>
                  <Text strong className="access-label">
                    Access to Vertical/s
                    <br />
                    <Text type="secondary" className="access-hint">(select ALL that apply)</Text>
                  </Text>
                  <SbSelect
                    mode="multiple"
                    value={selectedVerticals}
                    onChange={(val) => handleHierarchyChange('vertical', val)}
                    options={verticalOptions}
                    disabled={isDefault || selectedBusinessUnits.length === 0}
                    placeholder="Select Vertical"
                    maxTagCount="responsive"
                  />
                </AccessRowStyled>

                <AccessRowStyled>
                  <Text strong className="access-label">
                    Access to Teams/s
                    <br />
                    <Text type="secondary" className="access-hint">(select ALL that apply)</Text>
                  </Text>
                  <SbSelect
                    mode="multiple"
                    value={selectedTeams}
                    onChange={setSelectedTeams}
                    options={teamOptions}
                    disabled={isDefault || selectedVerticals.length === 0}
                    placeholder="Select Teams"
                    maxTagCount="responsive"
                  />
                </AccessRowStyled>
              </AccessSelectorsWrapperStyled>
            </>
          )}

          {/* ── Features / Access checkbox table ── */}
          <EntitlementListWrapperStyled>
            <SbTable
              columns={entitlementColumns}
              dataSource={sortedGroups}
              rowKey={(record) => record.groupId}
              pagination={false}
              size="small"
              rowClassName="entitlement-group-row"
            />
          </EntitlementListWrapperStyled>
        </>
      )}
    </SbDrawer>
  );
};

export default ProfileDrawerV2;
